/**
 * Converts a string to title case (first letter of each word capitalized)
 * @param str - The string to convert
 * @returns The string in title case
 * @example
 * toTitleCase('hello world') // 'Hello World'
 * toTitleCase('HELLO WORLD') // 'Hello World'
 */
export function toTitleCase(str: string): string {
  if (!str) return ''

  return str
    .toLowerCase()
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

/**
 * Parses a string or boolean value to a boolean
 * Handles common string representations of boolean values
 * @param value - The value to parse
 * @returns The parsed boolean value, or undefined if cannot be determined
 * @example
 * parseBoolean('true') // true
 * parseBoolean('false') // false
 * parseBoolean('1') // true
 * parseBoolean('0') // false
 * parseBoolean(true) // true
 */
export function parseBoolean(value: string | boolean | undefined | null): boolean | undefined {
  if (value === undefined || value === null) return undefined
  if (typeof value === 'boolean') return value

  const normalized = value.toString().toLowerCase().trim()

  if (normalized === 'true' || normalized === '1' || normalized === 'yes') {
    return true
  }

  if (normalized === 'false' || normalized === '0' || normalized === 'no' || normalized === '') {
    return false
  }

  return undefined
}

/**
 * Parses a string to a number
 * @param value - The value to parse
 * @returns The parsed number, or undefined if not a valid number
 * @example
 * parseNumber('42') // 42
 * parseNumber('3.14') // 3.14
 * parseNumber('not a number') // undefined
 */
export function parseNumber(value: string | number | undefined | null): number | undefined {
  if (value === undefined || value === null) return undefined
  if (typeof value === 'number') return value

  const num = Number(value)
  return isNaN(num) ? undefined : num
}

/**
 * Normalizes a string by trimming whitespace and converting to lowercase
 * @param str - The string to normalize
 * @returns The normalized string
 * @example
 * normalizeString('  Hello World  ') // 'hello world'
 * normalizeString('HELLO') // 'hello'
 */
export function normalizeString(str: string | undefined | null): string {
  if (str === undefined || str === null) return ''
  return str.trim().toLowerCase()
}

/**
 * Converts an arbitrary string to snake_case.
 *
 * Handles:
 * - camelCase and PascalCase boundaries (e.g., "helloWorld" -> "hello_world", "URLValue" -> "url_value")
 * - spaces, hyphens, and other non-alphanumeric separators
 * - numeric boundaries (e.g., "Plan 12 Month" -> "plan_12_month")
 * - trimming duplicate/leading/trailing underscores
 * - null/undefined by returning an empty string
 *
 * Kept intentionally small and single-purpose.
 *
 * @param value - The input string to convert (can be null or undefined)
 * @returns The snake_cased string or an empty string when input is null/undefined
 *
 * @example
 * toSnakeCase('Hello World') // 'hello_world'
 * toSnakeCase('helloWorld') // 'hello_world'
 * toSnakeCase('URLValue') // 'url_value'
 * toSnakeCase('  plan-12 month ') // 'plan_12_month'
 */
export function toSnakeCase(value: string | null | undefined): string {
  if (!value) {
    return ''
  }

  // Normalize whitespace and prepare for boundary insertion
  let normalized: string = value.trim()

  // Insert underscore between lower/digit and upper (e.g., helloWorld -> hello_World)
  normalized = normalized.replace(/([a-z0-9])([A-Z])/g, '$1_$2')

  // Insert underscore in acronym-to-word boundary (e.g., URLValue -> URL_Value)
  normalized = normalized.replace(/([A-Z]+)([A-Z][a-z0-9]+)/g, '$1_$2')

  // Replace any run of non-alphanumeric chars with underscore
  normalized = normalized.replace(/[^a-zA-Z0-9]+/g, '_')

  // Collapse multiple underscores
  normalized = normalized.replace(/_+/g, '_')

  // Trim leading/trailing underscores
  normalized = normalized.replace(/^_+|_+$/g, '')

  return normalized.toLowerCase()
}
